import { format, isBefore, isSameDay, startOfDay } from "date-fns";
import React, { useCallback, useRef } from "react";
import { useDetectOutsideClick } from "../../../hooks/useDetectOutsideClick";

import { ShiftCalendarItem, ShiftCellProps } from "../types/shift-assignment.types"; 

import ShiftActionMenu from "./shift-action-menu";
import QuickShiftSelector from "./shift-quick-selector";

const SHIFT_CODE_EVENING = 'CA_TOI';
const SHIFT_CODE_AFTERNOON = 'CA_CHIEU';
const SHIFT_CODE_OTHER = 'CA_GAY';

const ShiftCell: React.FC<ShiftCellProps> = ({ 
    shift,
    day, 
    employeeId,
    shifts: allShifts,
    isLoadingShifts,
    onOpenCreateModal, 
    isActive, 
    onOpenPopover, 
    onShiftSelect, 
    activeActionMenu, 
    onOpenActionMenu, 
    onRemoveSingle, 
    onRemoveMultiple, 
    openUpwards 
}) => {
    const today = startOfDay(new Date());
    const isPast = isBefore(day, today);
    
    const isShiftSet = shift && shift.shift && shift.shift.id !== '0'; 

    const isActionMenuActive = activeActionMenu && isSameDay(activeActionMenu.day, day) && activeActionMenu.employeeId === employeeId;

    const quickShiftPopoverRef = useRef<HTMLDivElement>(null);
    useDetectOutsideClick(quickShiftPopoverRef, () => {
        if (isActive) {
            onOpenPopover(day, employeeId);
        }
    });

    const shiftActionMenuRef = useRef<HTMLDivElement>(null);
    useDetectOutsideClick(shiftActionMenuRef, () => {
        if (isActionMenuActive) {
            onOpenActionMenu(day, employeeId);
        }
    });

    const handleQuickShiftButtonClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        onOpenPopover(day, employeeId);
    }, [day, employeeId, onOpenPopover]);

    const popoverClasses = openUpwards ? 'bottom-full mb-1' : 'mt-1';


    // 1. TRƯỜNG HỢP 1: NGÀY ĐÃ QUA VÀ CHƯA GÁN CA
    if (isPast && !isShiftSet) {
        return (
            <div
                className="flex items-center justify-center w-full h-10 border rounded-md text-gray-500 bg-gray-100 border-gray-300 cursor-default"
                title={`Ngày đã qua, chưa phân công.`}
            >
                <span className="mr-1">🕒</span> Đã qua
            </div>
        );
    }

    // 2. TRƯỜNG HỢP 2: CA ĐÃ GÁN (Kích hoạt Action Menu)
    if (isShiftSet) {
        let baseBgColor = 'bg-green-100 border-green-400/70 text-green-800'; // Màu mặc định cho các ca khác

        if (shift?.shift.code === SHIFT_CODE_AFTERNOON) {
            baseBgColor = 'bg-yellow-100 border-yellow-400/70 text-yellow-800'; // Màu vàng cho ca chiều
        } else if (shift?.shift.code === SHIFT_CODE_EVENING) {
            baseBgColor = 'bg-purple-100 border-purple-400/70 text-purple-800'; // Màu tím cho ca tối
        } else if (shift?.shift.code === SHIFT_CODE_OTHER) {
            baseBgColor = 'bg-red-100 border-red-400/70 text-red-800'; // Màu đỏ cho ca gãy
        }

        const isPastAndSet = isPast;

        const pastClasses = isPastAndSet ? 'opacity-60 border-gray-400/50' : '';
        const activeClasses = isActionMenuActive ? 'shadow-lg ring-2 ring-blue-500 z-10' : '';

        return (
            <div ref={shiftActionMenuRef} className="relative">
                <div
                    className={`p-2 w-full text-center rounded-md border text-sm cursor-pointer transition ${baseBgColor} ${pastClasses} ${activeClasses} assigned-shift-cell`}
                    title={`Chi tiết ca làm việc ngày ${format(day, 'dd/MM')}`}
                    onClick={() => onOpenActionMenu(day, employeeId)}
                >
                    <p className="font-semibold">{shift!.shift.name}</p>
                    <p className="text-xs">{`${shift!.shift.start_time} - ${shift!.shift.end_time}`}</p> 
                </div>

                {isActionMenuActive && (
                    <div className={`absolute z-20 w-48 bg-white border border-gray-300 rounded-lg shadow-lg right-0 ${popoverClasses}`}>
                        <ShiftActionMenu
                            onClose={() => onOpenActionMenu(day, employeeId)}
                            onRemoveSingle={onRemoveSingle}
                            onRemoveMultiple={onRemoveMultiple} openUpwards={false}                        />
                    </div>
                )}
            </div>
        );
    }

    // 3. TRƯỜNG HỢP 3: CHƯA GÁN CA VÀ CHƯA QUA NGÀY (Hiển thị dấu '+')
    if (!isPast && !isShiftSet) {
        return (
            <div ref={quickShiftPopoverRef} className="relative">
                <button
                    className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center quick-shift-button"
                    onClick={handleQuickShiftButtonClick}
                >
                    +
                </button>

                {isActive && (
                    <div className={`absolute z-40 w-48 bg-white border border-gray-300 rounded-lg shadow-lg origin-top-right right-0 quick-shift-popover-container ${popoverClasses}`}>
                        <QuickShiftSelector
                            onSelectShift={(shiftType) => onShiftSelect(shiftType, day, employeeId)}
                            shifts={allShifts} 
                            isLoading={isLoadingShifts}
                            onClose={() => onOpenPopover(day, employeeId)}
                        />
                    </div>
                )}
            </div>
        );
    }

    return null;
};

export default React.memo(ShiftCell);